import React from 'react';
import { 
  MapPin, 
  Heart,
  Fuel,
  Calendar,
  Gauge,
  Power,
  Car,
  Box,
  Cog,
  User,
  Medal
} from 'lucide-react';

const ListItem = ({ 
  listing, 
  onNavigate, 
  onFavorite, 
  isFav, 
  message 
}) => {
  return (
    <div
      onClick={() => onNavigate(listing.id)}
      className={`bg-white rounded-xl shadow-md overflow-hidden cursor-pointer 
                  hover:shadow-xl transition-all duration-300 relative group
                  ${listing.featured ? 'border-4 border-green-600' : 'border border-gray-200'}`}
    >
      <div className="flex flex-col sm:flex-row h-full">
        {/* Zdjęcie */}
        <div className="w-full sm:w-[420px] h-[240px] sm:h-[280px] relative overflow-hidden">
          <img
            src={listing.image}
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          {/* Serduszko na zdjęciu */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onFavorite(listing.id);
            }}
            className="absolute top-3 right-3 p-2 hover:scale-110 transition-transform duration-200 
                     bg-white rounded-full shadow-lg z-10"
          >
            <Heart
              className={`w-6 h-6 ${isFav ? 'fill-red-500 stroke-red-500' : 'stroke-gray-400'}`}
            />
          </button>
          {listing.featured && (
            <div className="absolute top-3 left-3 bg-green-600 text-white px-3 py-1.5 text-sm rounded-full font-medium flex items-center gap-1.5 shadow-lg">
              <Medal className="w-4 h-4" />
              WYRÓŻNIONE
            </div>
          )}
        </div>

        {/* Główne informacje */}
        <div className="flex-grow p-5 flex flex-col">
          <div className="mb-4">
            <h3 className="text-xl font-bold mb-1">{listing.title}</h3>
            <p className="text-gray-600">{listing.subtitle}</p>
          </div>

          {/* Parametry w grid */}
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Fuel className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Paliwo</div>
                  <div className="font-medium">{listing.fuel}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Gauge className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Przebieg</div>
                  <div className="font-medium">{listing.mileage}</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Box className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Pojemność</div>
                  <div className="font-medium">{listing.engineCapacity}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Rok</div>
                  <div className="font-medium">{listing.year}</div>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Power className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Moc</div>
                  <div className="font-medium">{listing.power}</div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Car className="w-5 h-5 text-gray-900" />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Napęd</div>
                  <div className="font-medium">{listing.drive}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-4">
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                <Cog className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Skrzynia</div>
                <div className="font-medium">{listing.gearbox}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Prawa kolumna */}
        <div className="w-full sm:w-[220px] p-5 flex flex-col justify-between bg-gray-50">
          {/* Cena */}
          <div className="bg-green-600 rounded-lg p-5 shadow-lg">
            <div className="text-white text-sm mb-2">Cena</div>
            <div className="text-3xl font-bold text-white tracking-tight">
              {listing.price.toLocaleString('pl-PL')} zł
            </div>
          </div>

          {/* Dolna część prawej kolumny */}
          <div className="space-y-3">
            {/* Info o sprzedawcy */}
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Sprzedawca</div>
                <div className="font-medium">{listing.sellerType}</div>
              </div>
            </div>

            {/* Lokalizacja */}
            <div className="flex items-center gap-2 text-gray-700">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-gray-900" />
              </div>
              <div>
                <div className="text-sm text-gray-500">Lokalizacja</div>
                <div className="font-medium">
                  {listing.city}
                  <div className="text-sm text-gray-500">({listing.location})</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className="absolute top-3 right-3 bg-black bg-opacity-75 text-white text-sm px-3 py-1.5 rounded-full">
          {message}
        </div>
      )}
    </div>
  );
};

export default ListItem;